// Tile image cache - fetches and caches per-cell background tile images
// Uses IndexedDB for persistence across sessions, in-memory Map for fast rendering

import { getBackendUrl } from '../lib/api.js';
import { authState } from '../lib/auth.js';

const DB_NAME = 'd3-image-cache';
const STORE_NAME = 'images';
const BATCH_SIZE = 1024;

/** @type {Map<string, HTMLImageElement>} */
const memoryCache = new Map();

/** @type {IDBDatabase | null} */
let db = null;

/** @type {string | null} */
let currentGameId = null;
let spectatorMode = false;

/** @type {Set<string>} */
const pendingKeys = new Set();

/** @type {boolean} */
let batchQueued = false;

/** @type {Array<{col: number, row: number}>} */
let batchQueue = [];

/**
 * Open the IndexedDB database
 * @returns {Promise<IDBDatabase>}
 */
function openDB() {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get a cached image from IndexedDB
 * @param {string} key
 * @returns {Promise<string | undefined>} base64 data
 */
async function getFromDB(key) {
  const database = await openDB();
  return new Promise((resolve) => {
    const tx = database.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result?.base64Data);
    request.onerror = () => resolve(undefined);
  });
}

/**
 * Store an image in IndexedDB
 * @param {string} key
 * @param {string} base64Data
 */
async function putToDB(key, base64Data) {
  const database = await openDB();
  const tx = database.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  store.put({ key, base64Data, timestamp: Date.now() });
}

/**
 * Convert base64 string to HTMLImageElement
 * @param {string} base64Data
 * @returns {Promise<HTMLImageElement>}
 */
function base64ToImage(base64Data) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    // Data may already have the data: prefix or may be raw base64
    if (base64Data.startsWith('data:')) {
      img.src = base64Data;
    } else {
      img.src = `data:image/jpeg;base64,${base64Data}`;
    }
  });
}

/**
 * Get the cache key for a cell
 * @param {number} col
 * @param {number} row
 * @returns {string}
 */
function cellKey(col, row) {
  return `${col},${row}`;
}

/**
 * Get the DB key for a cell (includes game ID)
 * @param {number} col
 * @param {number} row
 * @returns {string}
 */
function dbKey(col, row) {
  return `${currentGameId}:${col},${row}`;
}

/**
 * Initialize the tile cache for a game
 * @param {string} gameId
 * @param {{ spectator?: boolean }} [options] - When `spectator` is true,
 *   tile fetches go through the staff-only `/spectate-image-batcher`
 *   endpoint that bypasses the per-game player check and the per-cell
 *   visibility filter. The spectator's frozen replay state has cells
 *   that aren't in the live snapshot's union, so the regular endpoint
 *   would reject them as not_visible.
 */
export function initTileCache(gameId, options = {}) {
  currentGameId = gameId;
  spectatorMode = !!options.spectator;
  memoryCache.clear();
  pendingKeys.clear();
  batchQueue = [];
  batchQueued = false;
}

/**
 * Get a cached tile image (memory only, for rendering)
 * @param {number} col
 * @param {number} row
 * @returns {HTMLImageElement | undefined}
 */
export function getTileImage(col, row) {
  return memoryCache.get(cellKey(col, row));
}

/**
 * Request tile images for a set of visible cells.
 * Checks memory cache, then IndexedDB (in parallel), then fetches from server in batch.
 * All images are added to the memory cache atomically so they appear in one frame.
 * @param {Array<{col: number, row: number}>} cells
 */
export async function loadTilesForCells(cells) {
  if (!currentGameId || cells.length === 0) return;

  // Filter to cells not already cached or pending
  const toCheck = [];
  for (const { col, row } of cells) {
    const key = cellKey(col, row);
    if (memoryCache.has(key) || pendingKeys.has(key)) continue;
    pendingKeys.add(key);
    toCheck.push({ col, row });
  }

  if (toCheck.length === 0) return;

  // Check IndexedDB in parallel for all cells
  const dbResults = await Promise.all(
    toCheck.map(async ({ col, row }) => {
      const cached = await getFromDB(dbKey(col, row));
      return { col, row, cached };
    }),
  );

  // Decode cached images in parallel, collect uncached cells
  const toFetch = [];
  const decodePromises = [];

  for (const { col, row, cached } of dbResults) {
    const key = cellKey(col, row);
    if (cached) {
      decodePromises.push(
        base64ToImage(cached)
          .then((img) => ({ key, img }))
          .catch(() => {
            toFetch.push({ col, row });
            return null;
          })
          .finally(() => pendingKeys.delete(key)),
      );
    } else {
      toFetch.push({ col, row });
    }
  }

  // Wait for all IndexedDB images to decode, then add them all at once
  const decoded = await Promise.all(decodePromises);
  for (const result of decoded) {
    if (result) {
      memoryCache.set(result.key, result.img);
    }
  }

  if (toFetch.length > 0) {
    // Add to batch queue and schedule a flush
    batchQueue.push(...toFetch);
    if (!batchQueued) {
      batchQueued = true;
      // Small delay to accumulate requests
      setTimeout(flushBatchQueue, 50);
    }
  }
}

/**
 * Flush the batch queue - fetch all pending tiles from the server
 */
async function flushBatchQueue() {
  batchQueued = false;
  const coordinates = [...batchQueue];
  batchQueue = [];

  if (coordinates.length === 0) return;

  const backendUrl = getBackendUrl();
  const token = await authState.getAccessToken();

  // Process in chunks
  for (let i = 0; i < coordinates.length; i += BATCH_SIZE) {
    const chunk = coordinates.slice(i, i + BATCH_SIZE);

    try {
      const endpoint = spectatorMode ? 'spectate-image-batcher' : 'image-batcher';
      const response = await fetch(`${backendUrl}/api/games/${currentGameId}/${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coordinates: chunk.map((c) => ({ col: c.col, row: c.row })),
        }),
      });

      if (!response.ok) {
        console.error('Failed to fetch tile batch:', response.status);
        // Clear pending state so they can be retried
        for (const c of chunk) {
          pendingKeys.delete(cellKey(c.col, c.row));
        }
        continue;
      }

      const data = await response.json();

      // Decode all images in parallel, then add them all at once
      const decodeResults = await Promise.all(
        (data.images || []).map(
          /** @param {{col: number, row: number, image: string}} item */ async (item) => {
            const key = cellKey(item.col, item.row);
            try {
              const img = await base64ToImage(item.image);
              // Persist to IndexedDB (fire and forget)
              putToDB(dbKey(item.col, item.row), item.image);
              return { key, img };
            } catch (e) {
              console.error(`Failed to decode tile image for (${item.col}, ${item.row}):`, e);
              return null;
            }
          },
        ),
      );

      // Add all decoded images to memory cache atomically
      for (const result of decodeResults) {
        if (result) {
          memoryCache.set(result.key, result.img);
        }
      }

      // Clear pending for all coordinates in chunk
      for (const c of chunk) {
        pendingKeys.delete(cellKey(c.col, c.row));
      }
    } catch (error) {
      console.error('Tile batch fetch error:', error);
      for (const c of chunk) {
        pendingKeys.delete(cellKey(c.col, c.row));
      }
    }
  }
}

/**
 * Clear the in-memory cache (call on game exit)
 */
export function clearTileCache() {
  memoryCache.clear();
  pendingKeys.clear();
  batchQueue = [];
  currentGameId = null;
}
